/**
 * S-07 미션 허브 목 데이터·상태 (F-17/F-18, §5 · 지침 §2D).
 * 보상은 무료 팩만(§5.5.1) — IP/RP 직접 지급 표기 금지.
 * 미션명이 곧 조건(§2D) — 설명 문구(note) 없음. 진행은 `progress / goal`.
 * TODO(server): 미션 카탈로그·검증·클레임·20팩 게이지·주간 마일스톤 전부 서버 RPC.
 * 상태는 localStorage에 UTC 일 단위로 유지(자정 UTC 리셋 목).
 */
import { useCallback, useState } from "react";
import { DAILY_PACK_CAP } from "./mockdb";

export type MissionCategory = "daily" | "weekly" | "social" | "inapp";
export type MissionStatus = "todo" | "claimable" | "claimed";

export type Mission = {
  id: string;
  category: MissionCategory;
  /** 미션명 = 조건 (§2D) */
  title: string;
  /** 클레임 목표치 — 1 = 단발 미션 */
  goal: number;
  /** 보상 무료 팩 수 — "×n"으로만 표기 */
  packs: number;
  social?: "instagram" | "x";
};

export const MISSION_TABS: { id: MissionCategory; label: string }[] = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "social", label: "Social" },
  { id: "inapp", label: "In-app" },
];

/** §5.2.2 일일 + §5.3.2 주간 구성 — TODO(server): 서버 미션 카탈로그 */
export const MISSIONS: Mission[] = [
  { id: "d-checkin", category: "daily", title: "Daily check-in", goal: 1, packs: 2 },
  { id: "d-assistant", category: "daily", title: "Pick today's assistant", goal: 1, packs: 2 },
  { id: "d-content", category: "daily", title: "View today's card content", goal: 1, packs: 1 },
  { id: "d-streak", category: "daily", title: "Keep your check-in streak", goal: 1, packs: 2 },
  { id: "i-vote", category: "inapp", title: "Vote in today's card art poll", goal: 1, packs: 1 },
  { id: "i-quiz", category: "inapp", title: "Answer the daily card quiz", goal: 1, packs: 1 },
  { id: "i-album", category: "inapp", title: "Pick today's card from your album", goal: 1, packs: 1 },
  { id: "s-ig-comment", category: "social", title: "Comment on today's Instagram post", goal: 1, packs: 5, social: "instagram" },
  { id: "s-ig-story", category: "social", title: "Share today's Instagram Story", goal: 1, packs: 2, social: "instagram" },
  { id: "s-x-reply", category: "social", title: "Reply to today's X post", goal: 1, packs: 3, social: "x" },
  { id: "w-att5", category: "weekly", title: "Attend 5 of 7 days", goal: 5, packs: 5 },
  { id: "w-att7", category: "weekly", title: "Attend 7 days straight", goal: 7, packs: 5 },
  { id: "w-ig", category: "weekly", title: "Comment on 3 different Instagram posts", goal: 3, packs: 5, social: "instagram" },
  { id: "w-x", category: "weekly", title: "Reply to 2 different X posts", goal: 2, packs: 3, social: "x" },
  { id: "w-collect", category: "weekly", title: "Collect 10 new card types", goal: 10, packs: 3 },
];

export type WeeklyMilestone = { at: number; packs: number };

/**
 * §2D 주간 마일스톤 트래커 노드 — 주간 미션 완료 수 임계점마다 보상.
 * TODO(server): 서버 마일스톤 정의로 교체.
 */
export const WEEKLY_MILESTONES: WeeklyMilestone[] = [
  { at: 2, packs: 2 },
  { at: 4, packs: 3 },
  { at: 5, packs: 5 },
];

/** 미수령 일일 보상 소멸 시점 목 — 다음 자정 UTC (§5.5.3). TODO(server): 서버 기준 시각 */
export function nextUtcMidnightIso(now: Date = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toISOString();
}

type Stored = {
  day: string;
  /** 미션별 진행 수 (0..goal) */
  progress: Record<string, number>;
  /** 수령 완료 미션 id */
  claimed: string[];
  /** 수령 완료 마일스톤 임계점 */
  milestones: number[];
  claimedPacks: number;
};

const STORAGE_KEY = "ty.renewal.missions.v2";

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function loadStored(): Stored {
  const fresh: Stored = { day: todayUtc(), progress: {}, claimed: [], milestones: [], claimedPacks: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fresh;
    const parsed = JSON.parse(raw) as Stored;
    // ponytail: 자정 UTC에 주간 미션까지 함께 리셋 — 서버 연동 시 주간은 주 단위로 분리
    if (parsed.day !== fresh.day || !parsed.progress) return fresh;
    return parsed;
  } catch {
    return fresh;
  }
}

export type ClaimResult = { ok: true; packs: number } | { ok: false; reason: string };
export type MissionWithState = Mission & { status: MissionStatus; progress: number };
export type MilestoneWithState = WeeklyMilestone & { claimed: boolean; claimable: boolean };

/**
 * 미션 상태 훅 — perform은 목 검증 통과(진행 +1), claim/claimAll은 일일
 * 20팩 한도(§5.5.4)를 넘기면 거부. 토스트/이동은 화면 책임.
 */
export function useMissions() {
  const [stored, setStored] = useState<Stored>(loadStored);

  const save = useCallback((next: Stored) => {
    setStored(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // 저장 실패는 무시 — 목 상태는 세션 내 유지로 충분
    }
  }, []);

  const perform = useCallback(
    (id: string) => {
      // TODO(server): 실제 미션 검증(출석/댓글/투표) 통과 후 진행 반영
      const goal = MISSIONS.find((m) => m.id === id)?.goal ?? 1;
      const cur = stored.progress[id] ?? 0;
      if (cur >= goal || stored.claimed.includes(id)) return;
      save({ ...stored, progress: { ...stored.progress, [id]: cur + 1 } });
    },
    [stored, save],
  );

  const claim = useCallback(
    (id: string): ClaimResult => {
      const mission = MISSIONS.find((m) => m.id === id);
      const done = mission && (stored.progress[id] ?? 0) >= mission.goal && !stored.claimed.includes(id);
      if (!mission || !done) return { ok: false, reason: "Nothing to claim" };
      if (stored.claimedPacks + mission.packs > DAILY_PACK_CAP) {
        return { ok: false, reason: `Daily cap reached — ${DAILY_PACK_CAP} packs per day` };
      }
      save({
        ...stored,
        claimed: [...stored.claimed, id],
        claimedPacks: stored.claimedPacks + mission.packs,
      });
      return { ok: true, packs: mission.packs };
    },
    [stored, save],
  );

  /** §2D 일괄 수령 — 수령 가능한 미션·마일스톤을 한도 내에서 전부 수령 */
  const claimAll = useCallback((): ClaimResult => {
    let gained = 0;
    let total = stored.claimedPacks;
    const claimed = [...stored.claimed];
    const milestones = [...stored.milestones];
    for (const m of MISSIONS) {
      if (claimed.includes(m.id) || (stored.progress[m.id] ?? 0) < m.goal) continue;
      if (total + m.packs > DAILY_PACK_CAP) continue;
      claimed.push(m.id);
      total += m.packs;
      gained += m.packs;
    }
    const weeklyDone = MISSIONS.filter(
      (m) => m.category === "weekly" && (stored.progress[m.id] ?? 0) >= m.goal,
    ).length;
    for (const ms of WEEKLY_MILESTONES) {
      if (milestones.includes(ms.at) || weeklyDone < ms.at) continue;
      if (total + ms.packs > DAILY_PACK_CAP) continue;
      milestones.push(ms.at);
      total += ms.packs;
      gained += ms.packs;
    }
    if (gained === 0) {
      return {
        ok: false,
        reason:
          total >= DAILY_PACK_CAP ? `Daily cap reached — ${DAILY_PACK_CAP} packs per day` : "Nothing to claim",
      };
    }
    save({ ...stored, claimed, milestones, claimedPacks: total });
    return { ok: true, packs: gained };
  }, [stored, save]);

  const missions: MissionWithState[] = MISSIONS.map((m) => {
    const progress = Math.min(m.goal, stored.progress[m.id] ?? 0);
    const status: MissionStatus = stored.claimed.includes(m.id)
      ? "claimed"
      : progress >= m.goal
        ? "claimable"
        : "todo";
    return { ...m, status, progress };
  });

  const weeklyDone = missions.filter((m) => m.category === "weekly" && m.status !== "todo").length;

  const milestones: MilestoneWithState[] = WEEKLY_MILESTONES.map((ms) => ({
    ...ms,
    claimed: stored.milestones.includes(ms.at),
    claimable: !stored.milestones.includes(ms.at) && weeklyDone >= ms.at,
  }));

  const claimablePacks =
    missions.filter((m) => m.status === "claimable").reduce((s, m) => s + m.packs, 0) +
    milestones.filter((m) => m.claimable).reduce((s, m) => s + m.packs, 0);

  return {
    missions,
    milestones,
    weeklyDone,
    todayPacks: stored.claimedPacks,
    claimablePacks,
    perform,
    claim,
    claimAll,
  };
}
